import React, { useState } from "react";
import { X } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { cn } from "../../../lib/utils";
import { FIELD } from "./classes";

/** Normalises a typed tag the way tags are compared: trimmed, lowercase, inner spaces as "-". */
export const normaliseTag = (raw) => String(raw || "").trim().toLowerCase().replace(/\s+/g, "-");

/**
 * Tags as removable chips plus a text field: Enter or comma adds, Backspace on an empty
 * field removes the last one. `suggestions` (the tenant's own tags) feed a datalist.
 */
export default function TagInput({ value = [], onChange, suggestions = [], placeholder, testId, label }) {
  const t = useT("crm");
  const [draft, setDraft] = useState("");
  const listId = testId ? `${testId}-suggestions` : undefined;
  const add = (raw) => {
    const tag = normaliseTag(raw);
    if (tag && !value.includes(tag)) onChange([...value, tag]);
    setDraft("");
  };
  return (
    <div className={cn(FIELD, "flex flex-wrap items-center gap-1.5 py-2")}>
      {value.map((tag) => (
        <span key={tag} className="inline-flex items-center gap-1 rounded-full border border-eh-border-soft bg-eh-cream px-2 py-0.5 text-xs text-eh-ink">
          {tag}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== tag))} aria-label={t("tags.remove", { tag })}
            className="rounded-full hover:text-eh-ink">
            <X size={12} aria-hidden="true" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        list={listId}
        aria-label={label}
        placeholder={value.length ? "" : placeholder}
        onChange={(e) => {
          const v = e.target.value;
          if (v.endsWith(",")) add(v.slice(0, -1)); else setDraft(v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); add(draft); }
          if (e.key === "Backspace" && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => { if (draft.trim()) add(draft); }}
        data-testid={testId}
        className="min-w-[6rem] flex-1 bg-transparent text-sm text-eh-ink outline-none placeholder:text-eh-muted"
      />
      {listId && (
        <datalist id={listId}>
          {suggestions.filter((s) => !value.includes(s)).map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </div>
  );
}
